import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton as MuiIconButton, InputBase, Stack } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback, useRef, useState } from "react";

import { Button } from "../components/Button";
import { MAX_FILE_SIZE, SUPPORTED_FILE_TYPES } from "./constants";

export interface ChatInputProps {
  onSend: (message: string) => void;
  onFileSelect?: (file: File) => void;
  onInviteToShift?: () => void;
  disabled?: boolean;
}

/**
 * Chat input with text field, invite-to-shift button, file upload, and send button.
 * Layout matches Figma Workplace Design System, node 20:682.
 */
export function ChatInput(props: ChatInputProps) {
  const { onSend, onFileSelect, onInviteToShift, disabled = false } = props;
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage("");
    }
  }, [message, onSend]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        showErrorToast("File size must be less than 10MB");
        return;
      }

      if (!SUPPORTED_FILE_TYPES.includes(file.type as (typeof SUPPORTED_FILE_TYPES)[number])) {
        showErrorToast("Unsupported file type. Please upload images, PDFs, or Word documents.");
        return;
      }

      onFileSelect?.(file);

      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFileSelect]
  );

  const hasMessage = Boolean(message.trim());

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 4,
        borderRadius: theme.borderRadius?.xLarge,
        backgroundColor: disabled
          ? theme.palette.background?.inactive
          : theme.palette.background?.baseDarker,
      })}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={SUPPORTED_FILE_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <InputBase
        fullWidth
        placeholder="Type your message..."
        value={message}
        disabled={disabled}
        autoComplete="off"
        spellCheck="true"
        inputProps={{
          "aria-label": "Chat message",
          maxLength: 5000,
        }}
        sx={(theme) => ({
          ...theme.typography.body2,
          // iOS auto-zooms inputs rendered below 16px on focus; keep touch devices at 16px.
          "@media (pointer: coarse)": {
            fontSize: "16px",
          },
          "& .MuiInputBase-input::placeholder": {
            color: theme.palette.base?.inactive,
            opacity: 1,
          },
        })}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        {onInviteToShift && (
          <Button
            aria-label="Invite to shift"
            disabled={disabled}
            size="small"
            startIconType="plus"
            variant="outlined"
            onClick={onInviteToShift}
          >
            Invite to shift
          </Button>
        )}

        <Stack direction="row" sx={{ alignItems: "center", gap: 2, marginLeft: "auto" }}>
          {onFileSelect && (
            <MuiIconButton
              aria-label="Attach file"
              disabled={disabled}
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                padding: "5px",
                "&:hover": {
                  color: theme.palette.text.primary,
                },
                "&.Mui-disabled": {
                  color: theme.palette.base?.inactive,
                },
              })}
              onClick={handleFileClick}
            >
              <AttachFileIcon sx={{ fontSize: "14px" }} />
            </MuiIconButton>
          )}
          <MuiIconButton
            aria-label="Send message"
            disabled={disabled || !hasMessage}
            sx={(theme) => ({
              backgroundColor: hasMessage && !disabled ? theme.palette.primary.main : "transparent",
              color: hasMessage && !disabled ? "white" : theme.palette.base?.inactive,
              borderRadius: theme.borderRadius?.xLarge,
              padding: "5px",
              minWidth: "24px",
              minHeight: "24px",
              "&:hover": {
                backgroundColor:
                  hasMessage && !disabled ? theme.palette.primary.dark : "transparent",
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: theme.palette.base?.inactive,
              },
            })}
            onClick={handleSend}
          >
            <SendIcon sx={{ fontSize: "14px" }} />
          </MuiIconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
